<template>
  <form-block :title="tr('Target audience')">
    <template #top-right>
      <refresh-button
        v-if="!entity.disconnectedAdAccount"
        :label="tr('Update audiences')"
        :success-message="tr('Saved and custom target audiences updated successfully.')"
        :promise="() => refreshAudiencesInSelectedAdAccount()"
      />
    </template>

    <info-text v-if="entity.disconnectedAdAccount" type="error">
      {{ tr('The selected ad account has been disconnected.') }}
    </info-text>
    <template v-else>
      <p class="m-0 text-sm text-muted-foreground" v-html="description" />

      <div class="flex flex-col gap-2">
        <label :class="[ 'flex w-fit items-center gap-2 text-sm font-medium text-foreground', isAudiencePlusDisabled || entity.output_campaign.hasSpecialAdCategories ? 'cursor-not-allowed' : 'cursor-pointer' ]">
          <ui-checkbox
            :model-value="!!entity.settings.adset_targeting_advantage_plus_audience"
            :disabled="isAudiencePlusDisabled || entity.output_campaign.hasSpecialAdCategories"
            @update:model-value="(value) => entity.settings.adset_targeting_advantage_plus_audience = value"
          />
          {{ tr('Advantage+ Audience') }}
        </label>

        <info-text v-if="advantagePlusMessage" :type="advantagePlusMessageType || 'info'">
          {{ advantagePlusMessage }}
        </info-text>
        <info-text v-if="entity.output_campaign.hasSpecialAdCategories">
          {{ tr('Advantage+ Audience disabled because Special Ad Categories were set in the campaign settings.') }}
        </info-text>
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Use saved target audiences') }}</ui-label>
        <multiple-audience-select
          v-model="entity.settings.adset_targeting_audiences"
          :external-audiences="savedAudiences"
          :disabled="disabled"
          :is-loading="isLoading"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Use custom audiences') }}</ui-label>
        <multi-select
          v-model="entity.settings.adset_included_custom_audiences"
          :options="customAudienceMultiSelectOptions"
          :loading="isLoading"
          :placeholder="tr('Search existing custom audiences')"
          :disabled="disabled || !canSelectCustomAudiences"
          color="var(--audience-include)"
          label-key="name"
          value-key="id"
          :disabled-option-tooltip="tr('The audience is empty')"
        />
      </div>

      <div v-if="canSelectCustomAudiences" class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Exclude') }}</ui-label>
        <multi-select
          v-model="entity.settings.adset_excluded_custom_audiences"
          :options="customAudienceMultiSelectOptionsForExclude"
          :loading="isLoading"
          :placeholder="tr('Search existing custom audiences')"
          :disabled="disabled"
          color="var(--audience-exclude)"
          label-key="name"
          value-key="id"
          :disabled-option-tooltip="tr('The audience is empty')"
        />
      </div>

      <info-text v-if="audiencePickerMessage" :type="audiencePickerMessageType || 'info'">
        {{ audiencePickerMessage }}
      </info-text>
      <info-text v-if="customAudienceRestriction" type="warning">
        {{ customAudienceRestriction.text }}
        <br v-if="customAudienceRestriction.link" />
        <a v-if="customAudienceRestriction.link" :href="customAudienceRestriction.link" target="_blank">
          {{ customAudienceRestriction.linkText }}
        </a>
        <small v-if="customAudienceRestriction.smallText">
          {{ customAudienceRestriction.smallText }}
        </small>
      </info-text>
    </template>
  </form-block>
</template>

<script>
import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import AudienceService from '@/services/Audience.js';
import InfoText from '@/ui/components/InfoText.vue';
import MultiSelect from '@/ui/components/MultiSelect.vue';
import MultipleAudienceSelect from '@/ui/components/MultipleAudienceSelect.vue';
import RefreshButton from '@/ui/components/RefreshButton.vue';

export default {
  name: 'TargetAudienceBlock',
  components: {
    InfoText,
    MultipleAudienceSelect,
    MultiSelect,
    RefreshButton,
  },
  mixins: [ RuleFormBlockMixin ],
  props: {
    advantagePlusAvailable: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    advantagePlusMessage: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    advantagePlusMessageType: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    audiencePickerMessage: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    audiencePickerMessageType: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    // Audiences the server marked unusable in the current configuration, each with
    // its own reason: [{ id, reason, applies_to_exclusion }]. Drives the picker grey-out + tooltip.
    disabledAudiences: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  computed: {
    isAudiencePlusDisabled() {
      if (!this.advantagePlusAvailable) {
        if (this.entity.settings.adset_targeting_advantage_plus_audience) {
          return false;
        }
        return true;
      }
      return false;
    },
    facebookAudienceManagerLink() {
      if (!this.entity.selectedAdAccount) {
        return null;
      }

      const id = this.entity.selectedAdAccount.external_id.split('_').pop();
      return 'https://adsmanager.facebook.com/adsmanager/audiences?act=' + id;
    },
    description() {
      if (!this.facebookAudienceManagerLink) {
        return null;
      }

      const here = '<a href="' + this.facebookAudienceManagerLink + '" target="_blank" class="font-semibold text-primary underline underline-offset-2">' + this.tr('here') + '</a>';

      return this.tr(
        'You can create your target audience(s) in Facebook Business Manager. ' +
        'If you have already created them before, you can select them from the dropdown list to use. ' +
        'If you haven`t created any target audience yet, you can do so by clicking :here',
        { ':here': here },
      );
    },
    isLoading() {
      return this.$store.getters['ruleForm/loadingAudiences'];
    },
    audiences() {
      return this.$store.getters['ruleForm/audiences'];
    },
    disabledAudienceReasons() {
      const map = {};
      for (const item of this.disabledAudiences || []) {
        map[item.id] = item.reason;
      }
      return map;
    },
    disabledAudienceReasonsForExclude() {
      const map = {};
      for (const item of this.disabledAudiences || []) {
        if (item.applies_to_exclusion !== false) {
          map[item.id] = item.reason;
        }
      }
      return map;
    },
    customAudiences() {
      return this.getAudiencesByType('custom', this.disabledAudienceReasons);
    },
    customAudienceMultiSelectOptions() {
      return this.toMultiSelectOptions(this.customAudiences);
    },
    customAudienceMultiSelectOptionsForExclude() {
      return this.toMultiSelectOptions(this.getAudiencesByType('custom', this.disabledAudienceReasonsForExclude));
    },
    savedAudiences() {
      return this.getAudiencesByType('saved', this.disabledAudienceReasons);
    },
    canSelectCustomAudiences() {
      const selectedAccount = this.entity.selectedAdAccount;
      return selectedAccount && selectedAccount.tos_accepted;
    },
    customAudienceRestriction() {
      const adAccountExternalId = this.entity.selectedAdAccount?.external_id;
      if (!this.canSelectCustomAudiences && adAccountExternalId) {
        const externalId = adAccountExternalId.replace('act_', '');
        return {
          text: this.tr('The Custom Audience terms of service have not been accepted for this ad account. You must accept them before selecting custom audiences.'),
          link: `https://www.facebook.com/customaudiences/app/tos/?act=${externalId}`,
          linkText: this.tr('Accept terms of services'),
          smallText: this.tr('Once you\'ve accepted the terms, it may take 1-2 hours for us to receive the updated information.'),
        };
      }

      return null;
    },
    selectedCustomAudiencesAreValid() {
      const included = this.entity.settings.adset_included_custom_audiences;
      const excluded = this.entity.settings.adset_excluded_custom_audiences;

      for (const item of included) {
        if (excluded.includes(item)) {
          return false;
        }
      }

      return true;
    },
  },
  watch: {
    selectedCustomAudiencesAreValid(val) {
      if (!val) {
        this.error('A custom audience cannot be used as included and excluded at the same time.');
      }
    },
    'entity.output_campaign.special_ad_categories': {
      deep: true,
      immediate: true,
      handler() {
        this.removeLookalikeIdsFromCustomAudienceSettings();
      },
    },
    isLoading(val) {
      if (!val) {
        this.$nextTick(() => this.removeLookalikeIdsFromCustomAudienceSettings());
      }
    },
  },
  methods: {
    toMultiSelectOptions(audiences) {
      return audiences.map(a => ({
        id: a.id,
        name: a.name,
        status: a.status,
        disabled: a.disabled,
        ...(a.disabled_message ? { disabled_message: a.disabled_message } : {}),
      }));
    },
    getAudiencesByType(type, reasons = {}) {
      const reasonMap = reasons;
      const audiences = this.audiences.filter(i => i.type === type);
      return audiences.map(audience => {
        const audienceCopy = { ...audience };
        const disabledReason = reasonMap[audience.id];
        if (disabledReason) {
          audienceCopy.disabled = true;
          audienceCopy.disabled_message = disabledReason;
        }
        else {
          audienceCopy.disabled = false;
          audienceCopy.disabled_message = null;
        }
        return audienceCopy;
      });
    },
    removeLookalikeIdsFromCustomAudienceSettings() {
      if (!this.entity.output_campaign.hasSpecialAdCategories) {
        return;
      }
      const lookalikeIds = new Set(this.customAudiences.filter(a => a.is_lookalike).map(a => a.id));
      const strip = arr => arr.filter(id => !lookalikeIds.has(id));
      this.entity.settings.adset_included_custom_audiences = strip([ ...this.entity.settings.adset_included_custom_audiences ]);
      this.entity.settings.adset_excluded_custom_audiences = strip([ ...this.entity.settings.adset_excluded_custom_audiences ]);
    },
    validate() {
      if (!this.selectedCustomAudiencesAreValid) {
        this.error('A custom audience cannot be used as included and excluded at the same time.');
        return false;
      }

      if (this.audiencePickerMessageType === 'error') {
        this.error(this.audiencePickerMessage);
        return false;
      }

      return true;
    },
    refreshAudiencesInSelectedAdAccount() {
      if (!this.entity.ad_account.id) {
        return Promise.resolve();
      }

      this.infoToast(this.tr('We will start updating the custom audiences.') + ' ' + this.tr('Be patient, this may take up to a minute.'));

      return AudienceService.refresh(this.entity.ad_account.id)
        .then(async () => {
          const resp = await this.$store.dispatch('ruleForm/fetchAudiences', this.entity.ad_account.id);
          return resp;
        });
    },
  },
};
</script>
